import {
  alpha,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import {
  SalesOverviewStats,
  SalesTeamSummary,
} from "../../../store/services/salesManagerService";
import ActivityTrendsChart from "../charts/ActivityTrendsChart";
import SubtypeBarChart from "../charts/SubtypeBarChart";
import {
  CATEGORY_LABELS,
  SUBTYPE_ORDER_COMMUNICATION,
  SUBTYPE_ORDER_FOLLOWUP,
} from "../shared/breakdown";

interface ActivityTrendsTabProps {
  overview: SalesOverviewStats | undefined;
  teamSummary: SalesTeamSummary | undefined;
  isLoading: boolean;
}

const ActivityTrendsTab: React.FC<ActivityTrendsTabProps> = ({
  overview,
  teamSummary,
  isLoading,
}) => {
  const theme = useTheme();
  const breakdown = teamSummary?.Breakdown ?? overview?.Breakdown;
  const trends = teamSummary?.ActivityTrends ?? [];

  if (isLoading || !breakdown) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Skeleton
            variant="rectangular"
            height={420}
            sx={{ borderRadius: 3 }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Skeleton
            variant="rectangular"
            height={340}
            sx={{ borderRadius: 3 }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Skeleton
            variant="rectangular"
            height={340}
            sx={{ borderRadius: 3 }}
          />
        </Grid>
      </Grid>
    );
  }

  const cardSx = {
    borderRadius: 3,
    boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    height: "100%",
  };

  return (
    <Grid container spacing={3}>
      {/* Trendgrafen tar full bredd längst upp */}
      <Grid item xs={12}>
        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold">
              Schemalagda vs slutförda per dag
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Visar planerad belastning (schemalagda) mot faktisk leverans
              (slutförda) över tid.
            </Typography>
            <ActivityTrendsChart trends={trends} height={320} />
          </CardContent>
        </Card>
      </Grid>

      {/* Planering — Communication-kategori */}
      <Grid item xs={12} md={6}>
        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold">
              {CATEGORY_LABELS.Communication}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fördelning per subtyp. Dessa är intent/planerade aktiviteter —
              "slutförda" betyder att planeringstaksen är kvitterad.
            </Typography>
            <SubtypeBarChart
              subtypeBreakdown={breakdown.Total.Communication}
              subtypeOrder={SUBTYPE_ORDER_COMMUNICATION}
              height={320}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Mötesbokning — Followup-kategori */}
      <Grid item xs={12} md={6}>
        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold">
              {CATEGORY_LABELS.Followup}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Konkret bokade aktiviteter — representerar faktiskt utfört
              arbete per subtyp.
            </Typography>
            <SubtypeBarChart
              subtypeBreakdown={breakdown.Total.Followup}
              subtypeOrder={SUBTYPE_ORDER_FOLLOWUP}
              height={320}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ActivityTrendsTab;
